import { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { showSnackbar } from "@/lib/snackbarUtils";
import { PROMOTE_FROM_WAITLIST_MUTATION } from "../queries";
import Button from "@/components/Button";
import EmailPreviewDialog, {
  EMAIL_PREVIEW_MODAL_CLASSNAME,
  EmailPreviewModalActions
} from "@/components/EmailPreviewDialog";
import { usePromotedFromWaitlistEmailPreview } from "@/pages/events/_hooks/usePromotedFromWaitlistEmailPreview";
import Dialog from "@/components/Dialog";
import { useParams } from "react-router-dom";
import useAmplitude from "@/hooks/useAmplitude";
import { EVENT_ANALYTICS_EVENTS, getEventAnalyticsData } from "@/pages/events/_utils/amplitude";

interface OverrideModalProps {
  onClosed: () => void;
  onError: (errorMessage: string) => void;
  eventId: string;
  personId: string;
}

export default function OverrideModal({ onClosed, onError, eventId, personId }: OverrideModalProps) {
  const { eventSlug = "" } = useParams();
  const { trackEvent } = useAmplitude();

  const [promoteFromWaitlist, { loading: mutationLoading }] = useMutation(PROMOTE_FROM_WAITLIST_MUTATION);
  const [previewOpen, setPreviewOpen] = useState(false);
  const {
    loadPreview,
    resetPreview,
    preview,
    loading: previewLoading,
    error: previewError
  } = usePromotedFromWaitlistEmailPreview(eventId, personId);

  useEffect(() => () => resetPreview(), [resetPreview]);

  const handlePreviewEmail = () => {
    setPreviewOpen(true);
    void loadPreview();
  };

  async function handlePromote() {
    try {
      const { data } = await promoteFromWaitlist({
        variables: { eventId, personId }
      });
      const { success, message } = data.promoteFromWaitlist;
      const errorMessage = `The registration override could not be sent. Error: ${message}`;
      if (success) {
        showSnackbar("Registrant sent a registration override link", {
          close: true
        });
        trackEvent(
          EVENT_ANALYTICS_EVENTS.waitlistPromoted,
          getEventAnalyticsData({
            source: "waitlist",
            eventId,
            eventSlug,
            action: "promote"
          })
        );
      } else {
        onError(errorMessage);
      }
    } catch (e) {
      onError((e as Error).message);
    } finally {
      onClosed();
    }
  }

  return (
    <>
      <Dialog
        open
        onClosed={onClosed}
        headline="Send Registration Override"
        data-testid="override-modal"
        className={`${EMAIL_PREVIEW_MODAL_CLASSNAME} max-w-[560px] p-6 pr-2`}
        hasFormElements={false}
        actions={
          <EmailPreviewModalActions onPreview={handlePreviewEmail} previewDisabled={mutationLoading}>
            <Button variant="text" onClick={onClosed}>
              Cancel
            </Button>
            <Button disabled={mutationLoading} onClick={handlePromote}>
              Send Registration Override
            </Button>
          </EmailPreviewModalActions>
        }
      >
        <div className="flex items-center text-sys-brand-on-surface-variant">
          <p className="text-body-medium">
            Are you sure you want to email this person a registration link that{" "}
            <span className="font-bold">allows them to bypass this event’s attendance limits</span>? Once they register,
            they will be moved from the Waitlist onto the Registrations list. This cannot be undone.
          </p>
        </div>
      </Dialog>
      <EmailPreviewDialog
        open={previewOpen}
        onClosed={() => setPreviewOpen(false)}
        subject={previewLoading ? undefined : preview?.subject}
        html={previewLoading ? undefined : preview?.html}
        loading={previewLoading}
        errorMessage={previewLoading ? null : (previewError?.message ?? null)}
      />
    </>
  );
}
